import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  DialogActions,
} from '@mui/material';
import { useState } from 'react';

import { useMutation } from '@/hooks/mutationWithSnackbar';
import { graphql } from '@/gql';
import { Or2Button } from '@/components/button/Or2Button';
import { LoadableConfirmationButton } from '@/components/button/LoadableConfirmationButton';

const BULK_CREATE_CONVERSATIONS_MUTATION = graphql(`
  mutation BulkCreateOrtooGptConversations(
    $params: BulkCreateOrtooGptConversationsInput
  ) {
    bulkCreateOrtooGptConversations(params: $params) {
      conversationsConnection {
        edges {
          node {
            ortooGptConversation {
              _id
            }
          }
        }
      }
    }
  }
`);

interface Props {
  open: boolean;
  onClose: () => void;
}

export const BulkUploadDialog = ({ open, onClose }: Props) => {
  const [prompts, setPrompts] = useState('');
  const [repetitions, setRepetitions] = useState<number | ''>('');
  const [experimental, setExperimental] = useState(false);
  const [bulkCreateConversations, { loading }] = useMutation(
    BULK_CREATE_CONVERSATIONS_MUTATION,
    {
      completionMessage: 'Bulk upload successful',
      onCompleted: () => {
        onClose();
        setPrompts('');
      },
    },
  );

  const handleUpload = () => {
    const promptList = prompts
      .split('\n')
      .map((q) => q.trim())
      .filter((q) => q.length > 0)
      // reverse so that the last item is created first
      // i.e. the order in the site will match the order in the file
      .reverse();

    const repeatedPromptList = promptList.flatMap((prompt) =>
      Array(repetitions === '' ? 1 : Number(repetitions)).fill(prompt),
    );

    if (repeatedPromptList.length === 0) {
      return;
    }

    bulkCreateConversations({
      variables: {
        params: {
          conversations: repeatedPromptList.map((prompt) => ({
            prompt,
            experimental,
            fileIds: [],
          })),
        },
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk upload</DialogTitle>
      <DialogContent>
        <Typography>
          Please write your prompts in the text area below, with one prompt per
          line. You should be able to copy and paste your prompts from a
          spreadsheet column.
        </Typography>
        <Typography fontWeight="bold">
          You will need to wait a few seconds and refresh the page to see the
          new conversations.
        </Typography>
        <TextField
          label="Prompts"
          multiline
          rows={10}
          value={prompts}
          onChange={(e) => setPrompts(e.target.value)}
          sx={{
            marginTop: 2,
            width: '100%',
            // Long prompts scroll rather than wrap
            '& .MuiInputBase-inputMultiline': {
              whiteSpace: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
            },
          }}
        />
        <TextField
          label="Repetitions"
          type="number"
          value={repetitions}
          onChange={(e) => {
            setRepetitions(e.target.value ? Number(e.target.value) : '');
          }}
          sx={{
            marginTop: 2,
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={experimental}
              onChange={(_event, checked) => setExperimental(checked)}
              name="experimental"
            />
          }
          label="Use AI Assistant Experimental"
          sx={{ display: 'flex', marginTop: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Or2Button color="inherit" onClick={onClose}>
          Cancel
        </Or2Button>
        <LoadableConfirmationButton
          loading={loading}
          color="primary"
          onClick={handleUpload}
          disabled={!prompts.trim()}
        >
          Upload
        </LoadableConfirmationButton>
      </DialogActions>
    </Dialog>
  );
};
